import { getI18n } from 'react-i18next';

import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { Loading } from '@/components';
import { getUserInfo, toast } from '@/utils';
import { pickAppCommonParams } from '@/utils/requestParams';

export type TAxiosConfigExtra = {
  loading?: boolean;
  transformData?: (data: any) => any;
};

// 创建axios实例
const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_SERVER_URL, // 请求的默认前缀 只要是发出去请求就会 默认带上这个前缀
  timeout: 30000, // 请求超时时间：30s
});

// 重试次数，共请求3次
const retry = 0;
// 请求的间隙
const retryDelay = 1000;

// 请求拦截器
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig & TAxiosConfigExtra) => {
    const { params = {}, loading } = config;
    if (loading) Loading.show();

    const auth = await getUserInfo();
    const bridgePackageName = window.bridge ? auth?.packageName || '' : '';
    const authParams = { ...(auth ?? {}) };
    if (!window.bridge) delete authParams.packageName;
    config.params = {
      ...params,
      ...(params.uid ? {} : authParams),
      ...['lang', 'deviceId'].reduce<{ [x: string]: string }>((ret, d) => {
        switch (d) {
          case 'lang':
            ret.language = localStorage.getItem(d) || '';
            break;
          default:
            ret[d] = localStorage.getItem(d) || '';
        }
        return ret;
      }, {}),
      ...pickAppCommonParams(),
      ...(bridgePackageName ? { packageName: bridgePackageName } : {}),
    };
    return config;
  },
  (err: AxiosError & { config: TAxiosConfigExtra }) => {
    if (err.config.loading) {
      Loading.hide();
    }
    return Promise.reject(err);
  },
);

// 响应拦截器即异常处理
axiosInstance.interceptors.response.use(
  (res: AxiosResponse & { config: TAxiosConfigExtra }) => {
    const { code, message } = res.data as IResponse<unknown>;
    if (code !== 200) {
      toast(message);
    }
    if (res.config.loading) {
      Loading.hide();
    }
    if (res?.data?.data) {
      // 列表放回的顶层不加，特殊处理
      if ('whetherLastPage' in res.data.data && 'list' in res.data.data) {
        res.data.data = {
          ...res.data.data,
          dl_list: res.data.data.list,
          dl_whetherLastPage: res.data.data.whetherLastPage,
        };
        delete res.data.data.list;
        delete res.data.data.whetherLastPage;
      }
      res.data.data = handleResWithPrefix(res.data.data);
    }
    return res;
  },
  (err: AxiosError & { config: { retryCount: number } & TAxiosConfigExtra }) => {
    const { config, code, message } = err;

    if (config.loading) {
      Loading.hide();
    }

    //网络波动/cors跨域会进行重试
    if (code === 'ERR_NETWORK' || message === 'Network Error' || !window.navigator.onLine) {
      config.retryCount = config?.retryCount || 0;
      if (config.retryCount >= retry) {
        // 达到最大重试次数，直接返回错误
        toast(getI18n().t('error.systemFatal'));
        return Promise.reject(err);
      }

      config.retryCount++;

      // 创建新的 Promise 来进行重试
      return new Promise<void>((resolve) => {
        setTimeout(() => resolve(), retryDelay || 0);
      }).then(() => {
        return axiosInstance(config);
      });
    }
    //其他情况
    return Promise.reject(err);
  },
);

const handler = {
  get: function (target: any, prop: any) {
    if (prop === '__target__') {
      return target;
    }

    const prefix = import.meta.env.VITE_ATTRIBUTE_PREFIX;
    const prefixedKey = `${prefix}${prop}`;
    if (Object.prototype.hasOwnProperty.call(target, prefixedKey)) {
      return target[prefixedKey];
    }
    return target[prop];
  },
  set: function (target: any, prop: any, value: any) {
    const prefix = import.meta.env.VITE_ATTRIBUTE_PREFIX;
    target[`${prefix}${prop}`] = value;
    return true;
  },
};

export function handleResWithPrefix(data: any) {
  if (!data || typeof data !== 'object') return data;

  if (Array.isArray(data)) {
    data = data.map((item) => handleResWithPrefix(item));
    return data;
  }

  for (const key in data) {
    data[key] = handleResWithPrefix(data[key]);
  }
  return new Proxy(data, handler);
}

// 导出实例
export default axiosInstance;
